<template>
  <div class="overview-sources ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">rss_feed</i>
      <Tooltip :text="$t('dash.overview.help.sources')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.sources.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span v-if="sources" class="ov-chip-muted">{{ sources.length }}</span>
      <button v-if="canEdit" class="ov-arrow-link ov-block-link" @click="$emit('go', { name: 'sources' })">{{ $t('dash.overview.pages.manage') }}<i class="material-icons">arrow_forward</i></button>
    </div>

    <div v-if="!sources && loading" class="ov-skeleton-list">
      <div v-for="n in 4" :key="n" class="ov-skeleton-line"></div>
    </div>
    <p v-else-if="!sources && failed" class="ov-error">{{ $t('dash.overview.sources.error_loading') }}</p>
    <p v-else-if="!sources || !sources.length" class="ov-empty">{{ $t('dash.overview.sources.empty') }}</p>
    <template v-else>
      <div class="src-summary">
        <Tooltip v-for="s in summary" :key="s.key" :text="$t(`dash.overview.help.sources_${s.key}`)" position="top">
          <span class="src-stat" :class="`src-stat--${s.key}`"><strong>{{ s.count }}</strong>{{ $t(`dash.overview.sources.${s.key}`) }}</span>
        </Tooltip>
      </div>

      <ul class="src-list">
        <li v-for="source in sorted" :key="source.id" class="src-row" :class="{ active: current && current.id === source.id }" @click="openSource(source, $event)">
          <span class="src-dot" :class="`src-dot--${source.state}`"></span>
          <div class="src-main">
            <p class="src-name">{{ source.name || source.host }}</p>
            <small><span class="src-type">{{ typeLabel(source) }}</span> {{ originLabel(source) }} · {{ $t('dash.overview.sources.pages_count', { count: source.pages }) }}</small>
          </div>
          <Tooltip :text="stateHelp(source)" position="top">
            <span class="src-state" :class="`src-state--${source.state}`">{{ stateLabel(source) }}</span>
          </Tooltip>
          <Tooltip v-if="canEdit" :text="$t(source.state === 'disabled' ? 'dash.overview.sources.enable' : 'dash.overview.sources.relaunch')" position="top">
            <button
              class="ov-icon-btn"
              :class="{ 'ov-icon-btn--danger': source.state === 'disabled' }"
              :disabled="relaunching[source.id]"
              @click.stop="relaunch(source)"
            >
              <i class="material-icons" :class="{ spin: relaunching[source.id] }">{{ source.state === 'disabled' ? 'play_arrow' : 'refresh' }}</i>
            </button>
          </Tooltip>
        </li>
      </ul>
    </template>

    <!-- Pop-in flottant sous la source (sans fond grisé) : dernières news + réactivation / relance ; clic extérieur = fermeture -->
    <transition name="src-modal">
      <div v-if="current" ref="pop" class="src-modal" role="dialog" :style="popStyle">
        <div>
          <div class="src-modal-head">
            <span class="src-dot" :class="`src-dot--${current.state}`"></span>
            <div class="src-main">
              <p class="src-name">{{ current.name || current.host }}</p>
              <small>
                <span class="src-type">{{ typeLabel(current) }}</span>
                <a v-if="isRss(current)" :href="current.url" target="_blank" rel="noopener noreferrer">{{ current.url }}</a>
                <template v-else>{{ originLabel(current) }}</template>
              </small>
            </div>

          </div>

          <p class="src-modal-status" :class="`src-modal-status--${current.state}`">{{ stateHelp(current) }}</p>

          <div v-if="canEdit" class="src-modal-actions">
            <button class="src-pop-btn" :disabled="relaunching[current.id]" @click="relaunch(current)">
              <i class="material-icons" :class="{ spin: relaunching[current.id] }">{{ current.state === 'disabled' ? 'play_arrow' : 'refresh' }}</i>
              {{ $t(current.state === 'disabled' ? 'dash.overview.sources.enable' : 'dash.overview.sources.relaunch') }}
            </button>
            <Tooltip :text="$t(current.state === 'disabled' ? 'dash.overview.help.sources_enable' : 'dash.overview.help.sources_relaunch')" position="top">
              <i class="material-icons src-pop-help">info_outline</i>
            </Tooltip>
          </div>

          <p class="src-modal-subtitle">{{ $t('dash.overview.sources.last_news') }}</p>
          <p v-if="newsLoading && !currentNews" class="src-modal-loading"><i class="material-icons spin">sync</i></p>
          <p v-else-if="!currentNews || !currentNews.length" class="ov-empty">{{ $t('dash.overview.sources.no_news') }}</p>
          <ul v-else class="src-news">
            <li v-for="news in currentNews" :key="news._id || news.url">
              <a :href="news.url" target="_blank" rel="noopener noreferrer" class="src-news-item">
                <img v-if="news.enclosure" :src="news.enclosure" alt="" loading="lazy" referrerpolicy="no-referrer" @error="$event.target.style.display = 'none'" />
                <span v-else class="src-news-thumb"><i class="material-icons">description</i></span>
                <span class="src-news-text">
                  <span class="src-news-title">{{ news.title }}</span>
                  <small>{{ formatDate(news.published_at) }}</small>
                </span>
                <i class="material-icons src-news-open">open_in_new</i>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import Sources from '../../mixins/Sources'
import Tooltip from '../tooltip/tooltipInit.vue'
import { readSection, writeSection } from './cache'

const { VUE_APP_API_HOST } = process.env
// Même ordre que l'attention à porter : désactivées d'abord
const STATE_ORDER = { disabled: 0, active: 1 }

export default {
  name: 'OverviewSources',

  components: { Tooltip },

  mixins: [Sources],

  props: {
    userId: { type: String, default: null },
    canEdit: { type: Boolean, default: false }
  },

  data() {
    return {
      // Cache local affiché tout de suite, remplacé par la réponse de GET /sources?section=true
      sources: readSection(this.userId, 'sources'),
      loading: false,
      failed: false,
      relaunching: {},
      current: null,
      popStyle: {},
      // uid de source -> dernières news (cache local, rafraîchi à l'ouverture)
      news: readSection(this.userId, 'sourceNews') || {},
      newsLoading: false
    }
  },

  watch: {
    // Pop-in déplacé dans <body> : un parent transformé (animations de l'overview) fausserait position: fixed
    current(value) {
      if (!value) return
      this.$nextTick(() => {
        const pop = this.$refs.pop
        if (pop && pop.parentNode !== document.body) document.body.appendChild(pop)
      })
    }
  },

  computed: {
    // Même règle que la section Sources (Table.vue) : active seulement si status est vrai
    withState() {
      return (this.sources || []).map(s => ({ ...s, state: s.status ? 'active' : 'disabled' }))
    },

    sorted() {
      return [...this.withState].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || (a.name || '').localeCompare(b.name || ''))
    },

    summary() {
      const count = state => this.withState.filter(s => s.state === state).length
      return [
        { key: 'active', count: count('active') },
        { key: 'disabled', count: count('disabled') }
      ]
    },

    currentNews() {
      return this.current ? this.news[this.current.uid] || null : null
    }
  },

  mounted() {
    this.refresh()
    document.addEventListener('keydown', this.onKeydown)
    document.addEventListener('mousedown', this.onDocumentMousedown)
    window.addEventListener('scroll', this.onScroll, true)
  },

  beforeDestroy() {
    // Le pop-in vit dans <body> : on le retire explicitement si le composant disparaît pendant qu'il est ouvert
    const pop = this.$refs.pop
    if (pop && pop.parentNode === document.body) document.body.removeChild(pop)
    document.removeEventListener('keydown', this.onKeydown)
    document.removeEventListener('mousedown', this.onDocumentMousedown)
    window.removeEventListener('scroll', this.onScroll, true)
  },

  methods: {
    async refresh() {
      this.loading = true
      const res = await this.listSources({ section: true })
      this.loading = false
      const list = res && res.body && res.body.success && Array.isArray(res.body.sources) ? res.body.sources : null
      // Échec : le cache éventuel reste affiché, sinon message d'erreur
      this.failed = !list
      if (!list) return
      // Pages qui utilisent chaque source : bot.flux contient des uid de sources (cf. section Sources)
      let bots = []
      try {
        bots = localStorage.bots && localStorage.bots != 'undefined' ? JSON.parse(localStorage.bots) : []
      } catch (e) {
        bots = []
      }
      this.sources = list.map(s => ({
        id: s.id,
        uid: s.uid,
        name: s.name,
        url: s.url,
        host: this.host(s.url),
        type: s.type || 'rss',
        custom_field: s.custom_field || null,
        status: !!s.status,
        to_fetch_at: s.to_fetch_at || null,
        error: s.error || null,
        pages: bots.filter(b => Array.isArray(b.flux) && b.flux.includes(s.uid)).length
      }))
      writeSection(this.userId, 'sources', this.sources)
    },

    openSource(source, event) {
      if (this.current && this.current.id === source.id) {
        this.current = null
        return
      }
      // Positionné sous la ligne cliquée (au-dessus s'il manque de place en bas)
      const rect = event && event.currentTarget ? event.currentTarget.getBoundingClientRect() : null
      if (rect) {
        const width = Math.min(520, window.innerWidth - 32)
        const left = Math.max(16, Math.min(rect.left, window.innerWidth - width - 16))
        const below = window.innerHeight - rect.bottom
        this.popStyle = below > 380 || below > rect.top
          ? { top: `${rect.bottom + 6}px`, left: `${left}px`, width: `${width}px`, maxHeight: `${Math.max(240, below - 22)}px` }
          : { bottom: `${window.innerHeight - rect.top + 6}px`, left: `${left}px`, width: `${width}px`, maxHeight: `${Math.max(240, rect.top - 22)}px` }
      }
      this.current = source
      this.loadNews(source)
    },

    // Fermeture au clic extérieur (hors pop-in et hors ligne de source, qui gère son propre clic)
    onDocumentMousedown(event) {
      if (!this.current) return
      const pop = this.$refs.pop
      if (pop && pop.contains(event.target)) return
      if (event.target.closest && event.target.closest('.src-row')) return
      this.current = null
    },

    // Défilement de la page = fermeture (le pop-in est positionné en fixe) ; pas le défilement interne du pop-in
    onScroll(event) {
      if (!this.current) return
      const pop = this.$refs.pop
      if (pop && event && pop.contains(event.target)) return
      this.current = null
    },

    isRss(source) {
      return !source.type || source.type === 'rss'
    },

    // Libellé du type, comme le formulaire de la section Sources ; « Google News » plutôt que « News »
    typeLabel(source) {
      const type = source.type || 'rss'
      if (type === 'google-news') return this.$t('dash.overview.sources.type_google_news')
      const key = `dash.sources.${type}-source`
      return this.$te(key) ? this.$t(key) : type
    },

    /**
     * Origine lisible : URL pour un RSS ; pour les autres types, la saisie d'origine (custom_field)
     * plutôt que l'URL de flux reconstruite (cf. overlay/Sources.vue constructUrlFeed)
     */
    originLabel(source) {
      if (this.isRss(source)) return source.host
      if (source.type === 'google-news') {
        const lang = (source.url || '').match(/ceid=([A-Z]{2}):/)
        return `« ${source.custom_field || ''} »${lang ? ` (${lang[1]})` : ''}`
      }
      return source.custom_field || source.host
    },

    // Dernières news parsées, comme le pop-in de la section Sources (GET /posts/list-news)
    async loadNews(source) {
      if (!source.uid) return
      this.newsLoading = true
      try {
        const res = await this.$http.get(`${VUE_APP_API_HOST}/posts/list-news?feed=${encodeURIComponent(source.uid)}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        const list = res && res.body && Array.isArray(res.body.news) ? res.body.news : []
        const news = list.map(n => ({ _id: n._id, title: n.title, url: n.url, enclosure: typeof n.enclosure === 'string' ? n.enclosure : null, published_at: n.published_at }))
        this.$set(this.news, source.uid, news)
        writeSection(this.userId, 'sourceNews', this.news)
      } catch (e) {
        if (!this.news[source.uid]) this.$set(this.news, source.uid, [])
      } finally {
        this.newsLoading = false
      }
    },

    /**
     * Réactivation (source désactivée) ou relance (source active) : même appel que l'édition de la section Sources,
     * qui remet la source active, remet ses erreurs à zéro et relance le parsing
     */
    async relaunch(source) {
      if (this.relaunching[source.id]) return
      this.$set(this.relaunching, source.id, true)
      const wasDisabled = source.state === 'disabled'
      const res = await this.putSource({ id: source.id, name: source.name, url: source.url })
      this.$set(this.relaunching, source.id, false)
      const ok = res && res.body && res.body.success === true
      this.$vs.notify({
        time: 6000,
        title: ok ? this.$t('dash.notifications.success_title') : this.$t('dash.overview.sources.relaunch_failed'),
        text: ok ? this.$t(wasDisabled ? 'dash.notifications.source_enabled' : 'dash.overview.sources.relaunched') : ((res && res.body && res.body.statusText) || ''),
        color: ok ? 'primary' : 'danger'
      })
      if (!ok) return
      const index = this.sources.findIndex(s => s.id === source.id)
      if (index !== -1) this.$set(this.sources, index, { ...this.sources[index], status: true, error: null })
      if (this.current && this.current.id === source.id) this.current = { ...this.current, state: 'active', status: true, error: null }
      writeSection(this.userId, 'sources', this.sources)
      // Le parsing tourne en tâche de fond : les news arrivent après quelques secondes
      setTimeout(() => { if (this.current && this.current.id === source.id) this.loadNews(source) }, 8000)
    },

    onKeydown(event) {
      if (event.key === 'Escape') this.current = null
    },

    host(url) {
      try {
        return new URL(url).hostname.replace(/^www\./, '')
      } catch (e) {
        return url || ''
      }
    },

    formatDate(date) {
      if (!date) return ''
      const m = typeof date === 'number' && date < 1e12 ? moment.unix(date) : moment(date)
      return m.format('DD/MM/YYYY HH:mm')
    },

    stateLabel(source) {
      if (source.state === 'disabled') return this.$t('dash.overview.sources.state_disabled')
      if (!source.to_fetch_at) return this.$t('dash.overview.sources.state_ok')
      return moment(source.to_fetch_at).locale((this.$i18n && this.$i18n.locale) || 'fr').fromNow()
    },

    // Mêmes textes que la section Sources (dash.sources.*)
    stateHelp(source) {
      if (source.state === 'disabled') return `${this.$t('dash.sources.status_error')}${source.error ? ` : ${source.error}` : ''}`
      return source.to_fetch_at
        ? `${this.$t('dash.sources.to_fetch_at')} ${moment(source.to_fetch_at).format('DD/MM/YYYY HH:mm')}`
        : this.$t('dash.sources.status_running')
    }
  }
}
</script>

<style scoped>
.src-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}
.src-stat {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
  font-size: 12px;
  color: #555e75;
  border-radius: 20px;
  padding: 3px 10px;
  background: #f1edf5;
}
.src-stat strong {
  font-size: 14px;
  color: #121826;
}
.src-stat--active { background: #e6f9f0; }
.src-stat--disabled { background: #ffdede; }
.src-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  max-height: 420px;
  overflow-y: auto;
}
.src-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 8px;
  border-radius: 10px;
  cursor: pointer;
}
.src-row:hover {
  background: #f6f1fa;
}
.src-dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #1a9e5e;
}
.src-dot--disabled { background: #ff6969; }
.src-main {
  flex: 1;
  min-width: 0;
}
.src-name {
  font-size: 13px;
  font-weight: 600;
  color: #121826;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.src-main small {
  display: block;
  font-size: 11px;
  color: #9ba3b7;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.src-main small a {
  color: #9ba3b7;
}
.src-state {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  padding: 2px 8px;
  background: #e6f9f0;
  color: #1a9e5e;
  white-space: nowrap;
}
.src-state--disabled { background: #ffdede; color: #e04848; }

/* Pop-in flottant ancré sous la ligne (position calculée au clic), même style que les pop-in « À traiter » */
.src-modal {
  position: fixed;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #f1edf5;
  border-top: 3px solid #6b1f4d;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(107, 31, 77, 0.16);
  overflow: hidden;
}
.src-modal > div {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}
/* Hors de .section-overview une fois dans <body> : styles propres (police, bouton, aide) */
.src-modal,
.src-modal * {
  box-sizing: border-box;
}
.src-modal {
  font-size: 13px;
  color: #121826;
  text-align: left;
}
.src-pop-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 35.2px;
  padding: 0 14px;
  border: 1.6px solid #6b1f4d;
  border-radius: 5.6px;
  background: #6b1f4d;
  color: #fff;
  font-size: 13.6px;
  font-weight: 500;
  cursor: pointer;
}
.src-pop-btn:hover {
  opacity: 0.88;
}
.src-pop-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.src-pop-btn .material-icons {
  font-size: 16px;
}
.src-pop-help {
  font-size: 15px;
  color: #9ba3b7;
  cursor: help;
}
.src-modal .spin {
  animation: src-spin 1s linear infinite;
}
@keyframes src-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.src-modal ul {
  margin: 0;
}
.src-row.active {
  background: #f6f1fa;
}
.src-type {
  display: inline-block;
  margin-right: 2px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #6b1f4d;
  background: #f1edf5;
  border-radius: 20px;
  padding: 0 6px;
}
.src-modal-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f1edf5;
}
.src-modal-head .src-name {
  font-size: 15px;
}
.src-modal-status {
  font-size: 12.8px;
  border-radius: 8px;
  padding: 8px 10px;
  background: #e6f9f0;
  color: #1a9e5e;
}
.src-modal-status--disabled { background: #fdf2f2; color: #e04848; }
.src-modal-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.src-modal-subtitle {
  font-size: 11px;
  font-weight: 600;
  color: #555e75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.src-modal-loading {
  text-align: center;
  color: #ab6e93;
}
.src-news {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  overflow-y: auto;
}
.src-news-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px;
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
}
.src-news-item:hover {
  background: #f6f1fa;
}
.src-news-item img,
.src-news-thumb {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 5.6px;
  object-fit: cover;
}
.src-news-thumb {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1edf5;
  color: #6b1f4d;
}
.src-news-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.src-news-title {
  font-size: 13px;
  color: #121826;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.src-news-text small {
  font-size: 11px;
  color: #9ba3b7;
}
.src-news-open {
  flex-shrink: 0;
  font-size: 16px;
  color: #ab6e93;
}
.src-modal-enter-active,
.src-modal-leave-active {
  transition: opacity 0.15s ease;
}
.src-modal-enter,
.src-modal-leave-to {
  opacity: 0;
}
</style>
